import PageHero from "../components/PageHero";
import LeadForm from "../components/LeadForm";
import { COMPANY, telLink, buildWhatsAppLink } from "../lib/site";
import { Phone, Mail, MessageCircle, MapPin, Clock } from "lucide-react";
import { motion } from "framer-motion";

export default function ContactPage() {
  const items = [
    {
      icon: Phone,
      label: "Call us",
      lines: COMPANY.phones,
      hrefs: COMPANY.phones.map(telLink),
    },
    {
      icon: Mail,
      label: "Email us",
      lines: [COMPANY.email],
      hrefs: [`mailto:${COMPANY.email}`],
    },
    {
      icon: MessageCircle,
      label: "WhatsApp",
      lines: [COMPANY.whatsappDisplay],
      hrefs: [buildWhatsAppLink()],
      external: true,
    },
    {
      icon: MapPin,
      label: "Location",
      lines: [COMPANY.address, "Serving PAN India"],
    },
    {
      icon: Clock,
      label: "Hours",
      lines: ["Mon – Sat: 9:30 AM – 8:00 PM", "Sun: By appointment"],
    },
  ];

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Get in Touch"
        title="Let's talk about your next investment."
        subtitle="Our senior advisor will personally respond within the next business hour. Discretion guaranteed."
        image="https://images.unsplash.com/photo-1512917774080-9991f1c4c750?q=80&w=2070&auto=format&fit=crop"
      />

      <section className="relative py-20 md:py-28 px-6 md:px-10">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5 space-y-8">
            {items.map((it, i) => {
              const Icon = it.icon;
              return (
                <motion.div
                  key={it.label}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.05 }}
                  data-testid={`contact-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="flex gap-5"
                >
                  <div className="shrink-0 w-12 h-12 rounded-full border border-[#D4AF37]/40 flex items-center justify-center">
                    <Icon
                      className="w-5 h-5 text-[#D4AF37]"
                      strokeWidth={1.4}
                    />
                  </div>
                  <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-white/40">
                      {it.label}
                    </div>
                    <div className="mt-2 space-y-1">
                      {it.lines.map((ln, idx) =>
                        it.hrefs?.[idx] ? (
                          <a
                            key={ln}
                            href={it.hrefs[idx]}
                            target={it.external ? "_blank" : undefined}
                            rel={it.external ? "noreferrer" : undefined}
                            className="block text-white hover:text-[#D4AF37] transition-colors"
                          >
                            {ln}
                          </a>
                        ) : (
                          <div key={ln} className="text-white/80">
                            {ln}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="lg:col-span-7 glass-strong p-8 md:p-12">
            <div className="eyebrow mb-3">Send a message</div>
            <h2 className="font-serif text-3xl md:text-4xl text-white font-light mb-8">
              Tell us about your{" "}
              <span className="gold-gradient-text italic">goals</span>.
            </h2>
            <LeadForm testidPrefix="contact-lead" variant="contact-page" />
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="px-6 md:px-10 pb-24">
        <div
          data-testid="contact-map"
          className="max-w-7xl mx-auto h-[420px] glass-strong overflow-hidden relative"
        >
          <iframe
            title="S Mahadeva Properties Location"
            src="https://www.google.com/maps?q=Dholera+SIR+Gujarat&output=embed"
            width="100%"
            height="100%"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            style={{ border: 0, filter: "grayscale(0.6) contrast(1.1) invert(0.9) hue-rotate(180deg)" }}
          />
        </div>
      </section>
    </div>
  );
}
